import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/client';
import './WorkPerformanceStats.css';

// Performance tab (Work Performance). Data: GET /api/work-reports/performance?weeks=N →
// per employee { totalHours, totalUnits, weeklyPay, weeks:[{ weekStart, hours,
// production:{ small_bags, sets, wildmicr, kits, fertilizers }, norm:{ [type]: { shifts, units } } }] }.
//
// Units of different product types are not comparable (65,000 small bags vs 1,600 sets),
// and inside a type the products differ too (a 75-variety set takes far longer than a
// 10-variety one). So the comparison metric is:
//   · types with a per-product norm (sets, wildmicr, fertilizers — produce_per_shift):
//     "% of norm" = shift-equivalents produced ÷ shifts worked (hours / 8) × 100
//   · types without a norm (small bags, kits): units per hour
// always against the TEAM average for the same type. Colors: CVD-validated categorical
// slots in fixed order; legend always shown; values in text ink, color only on marks.

const TYPES = ['small_bags', 'sets', 'wildmicr', 'kits', 'fertilizers'];
const TYPE_COLORS = { small_bags: '#2a78d6', sets: '#eb6834', wildmicr: '#1baf7a', kits: '#eda100', fertilizers: '#e87ba4' };
const TYPE_LABELS = { small_bags: 'Small Bags', sets: 'Sets', wildmicr: 'Wildflowers & Microgreens', kits: 'Kits', fertilizers: 'Fertilizers' };
const NORM_TYPES = new Set(['sets', 'wildmicr', 'fertilizers']);
const SHIFT_HOURS = 8;
const SORTS = [['units', 'Units'], ['hours', 'Hours'], ['perf', 'Performance vs team'], ['pay', 'Earnings'], ['name', 'Name']];

const fmtN = n => Number(n || 0).toLocaleString('en-US');
const fmtCompact = n => {
    const v = Number(n || 0);
    if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
    if (Math.abs(v) >= 1e4) return `${(v / 1e3).toFixed(1).replace(/\.0$/, '')}K`;
    return fmtN(Math.round(v));
};
const fmtH = h => `${Number(h || 0).toFixed(1)}h`;
const fmtMoney = v => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtWeek = ws => (ws ? new Date(ws + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const r1 = v => Math.round(v * 10) / 10;
const pct = (a, b) => (b > 0 ? Math.round((a / b - 1) * 100) : null);
const fmtMetric = (kind, v) => (kind === 'norm' ? `${Math.round(v)}%` : `${r1(v)}`);
const metricUnit = kind => (kind === 'norm' ? 'of norm' : 'u/h');

// Stacked horizontal mix bar: 2px surface gaps between segments, 4px rounded ends.
const MixBar = ({ production, total, height = 8 }) => {
    if (!total) return <div className="wps-mix wps-mix--empty" style={{ height }} />;
    return (
        <div className="wps-mix" style={{ height }} role="img" aria-label={TYPES.filter(t => production[t] > 0).map(t => `${TYPE_LABELS[t]} ${fmtN(production[t])}`).join(', ')}>
            {TYPES.filter(t => production[t] > 0).map(t => (
                <span key={t} className="wps-mix__seg" style={{ width: `${(production[t] / total) * 100}%`, background: TYPE_COLORS[t] }}
                      title={`${TYPE_LABELS[t]}: ${fmtN(production[t])} (${Math.round((production[t] / total) * 100)}%)`} />
            ))}
        </div>
    );
};

// Thin bar of a value against a scale, with the team-average marker as a hairline.
const VsBar = ({ value, avg, max, color, kind }) => (
    <div className="wps-vs" title={`${fmtMetric(kind, value)} ${metricUnit(kind)} · team average ${fmtMetric(kind, avg)}`}>
        <span className="wps-vs__fill" style={{ width: `${Math.min(100, (value / max) * 100)}%`, background: color }} />
        {avg > 0 && <span className="wps-vs__avg" style={{ left: `${Math.min(100, (avg / max) * 100)}%` }} />}
    </div>
);

// units + norm of one type for one employee (all weeks)
const sumType = (e, type) => e.weeks.reduce((a, w) => {
    a.units += w.production[type] || 0;
    const n = w.norm && w.norm[type];
    if (n) { a.shifts += n.shifts || 0; a.normed += n.units || 0; }
    return a;
}, { units: 0, shifts: 0, normed: 0 });

// metric value for a type: % of norm when the type has norms and the employee's products carry
// one (ponytail: hours are not split by type — an employee making two types gets both metrics
// against their total hours), else units per hour
const metricOf = (type, sum, hours) => {
    if (!hours || sum.units <= 0) return null;
    if (NORM_TYPES.has(type) && sum.normed > 0) {
        // extrapolate the normed part to all units of the type when some products lack a norm
        const shifts = sum.shifts * (sum.units / sum.normed);
        return { kind: 'norm', value: (shifts / (hours / SHIFT_HOURS)) * 100, coverage: sum.normed / sum.units };
    }
    return { kind: 'uph', value: sum.units / hours, coverage: 1 };
};

const WorkPerformanceStats = ({ updateKey, search = '', employeeId = '' }) => {
    const [raw,      setRaw]      = useState([]);
    const [loading,  setLoading]  = useState(false);
    const [weeks,    setWeeks]    = useState(8);
    const [sort,     setSort]     = useState('units');
    const [expanded, setExpanded] = useState(new Set());

    useEffect(() => {
        setLoading(true);
        api.get(`/api/work-reports/performance?weeks=${weeks}`)
            .then(r => setRaw(r.data || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [weeks, updateKey]);

    // page-level search / employee filter (WorkPerformancePage)
    const filtered = useMemo(() => raw.filter(e =>
        (!employeeId || String(e.employeeID) === String(employeeId)) &&
        (!search || String(e.employeeName || '').toLowerCase().includes(search))
    ), [raw, search, employeeId]);

    // Team benchmarks on the FULL set, so a filtered view still compares against the whole team.
    // per type: { units, avg (metric), max (metric), kind }
    const team = useMemo(() => {
        const t = { hours: 0, units: 0, pay: 0, active: 0, production: {}, types: {} };
        const acc = {};
        for (const type of TYPES) { t.production[type] = 0; acc[type] = { shifts: 0, hoursNorm: 0, units: 0, hoursU: 0, max: 0, kind: NORM_TYPES.has(type) ? 'norm' : 'uph' }; }
        for (const e of raw) {
            t.hours += e.totalHours || 0; t.units += e.totalUnits || 0; t.pay += Number(e.weeklyPay) || 0;
            if (e.totalHours > 0) t.active++;
            for (const type of TYPES) {
                const s = sumType(e, type);
                t.production[type] += s.units;
                const m = metricOf(type, s, e.totalHours);
                if (!m) continue;
                const a = acc[type];
                if (m.kind === 'norm') { a.shifts += s.shifts * (s.units / s.normed); a.hoursNorm += e.totalHours; }
                else { a.units += s.units; a.hoursU += e.totalHours; }
                a.kind = m.kind; // a type falls back to u/h only when nobody's products carry a norm
                a.max = Math.max(a.max, m.value);
            }
        }
        for (const type of TYPES) {
            const a = acc[type];
            t.types[type] = { kind: a.kind, max: a.max,
                avg: a.kind === 'norm' ? (a.hoursNorm ? (a.shifts / (a.hoursNorm / SHIFT_HOURS)) * 100 : 0) : (a.hoursU ? a.units / a.hoursU : 0) };
        }
        return t;
    }, [raw]);

    const rows = useMemo(() => filtered.map(e => {
        const production = {};
        const types = [];
        for (const type of TYPES) {
            const s = sumType(e, type);
            production[type] = s.units;
            if (s.units <= 0) continue;
            const m = metricOf(type, s, e.totalHours);
            const tt = team.types[type];
            types.push({ type, units: s.units, metric: m, delta: m && tt.kind === m.kind ? pct(m.value, tt.avg) : null });
        }
        types.sort((a, b) => b.units - a.units);
        return { ...e, production, types, pay: Number(e.weeklyPay) || 0, lead: types[0] || null };
    }).sort((a, b) => {
        if (sort === 'name') return a.employeeName.localeCompare(b.employeeName);
        if (sort === 'hours') return b.totalHours - a.totalHours;
        if (sort === 'perf') return (b.lead?.delta ?? -999) - (a.lead?.delta ?? -999);
        if (sort === 'pay') return b.pay - a.pay;
        return b.totalUnits - a.totalUnits;
    }), [filtered, team, sort]);

    const showPay = raw.some(e => e.weeklyPay);
    const usedTypes = TYPES.filter(t => team.production[t] > 0);
    const toggle = id => setExpanded(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

    return (
        <div className={`wps-root${loading ? ' wps-root--loading' : ''}`}>
            {/* Filter row: period · sort · legend */}
            <div className="wps-toolbar">
                <span className="wps-toolbar__label">Period</span>
                {[4, 8, 12, 26].map(w => (
                    <button key={w} className={`wrt-week-btn${weeks === w ? ' active' : ''}`} onClick={() => setWeeks(w)}>{w}w</button>
                ))}
                <span className="wps-toolbar__label wps-toolbar__label--gap">Sort by</span>
                <select className="wps-select" value={sort} onChange={e => setSort(e.target.value)}>
                    {SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <div className="wps-legend" aria-label="Product types">
                    {TYPES.map(t => (
                        <span key={t} className={`wps-legend__item${usedTypes.includes(t) ? '' : ' wps-legend__item--off'}`}>
                            <span className="wps-legend__swatch" style={{ background: TYPE_COLORS[t] }} />{TYPE_LABELS[t]}
                        </span>
                    ))}
                </div>
            </div>

            {!loading && !raw.length && (
                <div className="wps-empty"><i className="fas fa-chart-bar" /><span>No production + shift data for the selected period</span></div>
            )}

            {raw.length > 0 && (
                <>
                    {/* KPI row */}
                    <div className="wps-kpis">
                        <div className="wps-glass wps-kpi">
                            <div className="wps-kpi__label">Hours worked</div>
                            <div className="wps-kpi__value">{fmtH(team.hours)}</div>
                            <div className="wps-kpi__sub">{team.active} employee{team.active === 1 ? '' : 's'} with shifts</div>
                        </div>
                        <div className="wps-glass wps-kpi">
                            <div className="wps-kpi__label">Units produced</div>
                            <div className="wps-kpi__value" title={fmtN(team.units)}>{fmtCompact(team.units)}</div>
                            <MixBar production={team.production} total={team.units} height={6} />
                            <div className="wps-kpi__mix">
                                {usedTypes.map(t => (
                                    <span key={t} title={TYPE_LABELS[t]}><span className="wps-dot" style={{ background: TYPE_COLORS[t] }} />{fmtCompact(team.production[t])}</span>
                                ))}
                            </div>
                        </div>
                        {showPay && (
                            <div className="wps-glass wps-kpi">
                                <div className="wps-kpi__label">Payroll for the period</div>
                                <div className="wps-kpi__value">{fmtMoney(team.pay)}</div>
                                <div className="wps-kpi__sub">{team.hours > 0 ? `${fmtMoney(team.pay / team.hours)} per hour on average` : ''}</div>
                            </div>
                        )}
                        <div className="wps-glass wps-kpi">
                            <div className="wps-kpi__label">Team pace <span className="wps-muted">· % of norm where products have one, else units/h</span></div>
                            <div className="wps-kpi__pace">
                                {usedTypes.map(t => (
                                    <div key={t} className="wps-kpi__pace-row">
                                        <span className="wps-dot" style={{ background: TYPE_COLORS[t] }} />
                                        <span className="wps-kpi__pace-name">{TYPE_LABELS[t]}</span>
                                        <b>{fmtMetric(team.types[t].kind, team.types[t].avg)}</b><span className="wps-muted"> {metricUnit(team.types[t].kind)}</span>
                                    </div>
                                ))}
                                {!usedTypes.length && <span className="wps-muted">—</span>}
                            </div>
                        </div>
                    </div>

                    {/* Employee cards */}
                    {!rows.length && <div className="wps-empty"><span>Nothing matches the search / employee filter</span></div>}
                    <div className="wps-cards">
                        {rows.map(e => {
                            const open = expanded.has(e.employeeID);
                            const lead = e.lead;
                            return (
                                <div key={e.employeeID} className={`wps-glass wps-card${open ? ' wps-card--open' : ''}`}>
                                    <button type="button" className="wps-card__head" onClick={() => toggle(e.employeeID)} aria-expanded={open}>
                                        <span className="wps-avatar">{e.employeeName.charAt(0).toUpperCase()}</span>
                                        <span className="wps-card__name">{e.employeeName}</span>
                                        <span className="wps-card__hours">{fmtH(e.totalHours)}</span>
                                        <i className={`fas fa-chevron-${open ? 'up' : 'down'} wps-card__chev`} />
                                    </button>

                                    <div className="wps-card__hero">
                                        <span className="wps-card__units" title={fmtN(e.totalUnits)}>{e.totalUnits > 0 ? fmtCompact(e.totalUnits) : '—'}</span>
                                        <span className="wps-card__units-label">{e.totalUnits > 0 ? 'units' : 'no production logged'}</span>
                                        {lead && lead.delta != null && (
                                            <span className={`wps-delta ${lead.delta >= 0 ? 'wps-delta--up' : 'wps-delta--down'}`}
                                                  title={`${TYPE_LABELS[lead.type]}: ${fmtMetric(lead.metric.kind, lead.metric.value)} ${metricUnit(lead.metric.kind)} vs team ${fmtMetric(lead.metric.kind, team.types[lead.type].avg)}`}>
                                                <i className={`fas fa-arrow-${lead.delta >= 0 ? 'up' : 'down'}`} /> {Math.abs(lead.delta)}% vs team
                                            </span>
                                        )}
                                    </div>
                                    <MixBar production={e.production} total={e.totalUnits} />

                                    {e.types.length > 0 && (
                                        <div className="wps-types">
                                            {e.types.map(t => {
                                                const tt = team.types[t.type];
                                                return (
                                                    <div key={t.type} className="wps-type">
                                                        <div className="wps-type__row">
                                                            <span className="wps-dot" style={{ background: TYPE_COLORS[t.type] }} />
                                                            <span className="wps-type__name">{TYPE_LABELS[t.type]}</span>
                                                            <span className="wps-type__units">{fmtN(t.units)}</span>
                                                            {t.metric && (
                                                                <span className="wps-type__uph" title={t.metric.kind === 'norm' && t.metric.coverage < 1 ? `norm known for ${Math.round(t.metric.coverage * 100)}% of these units` : undefined}>
                                                                    <b>{fmtMetric(t.metric.kind, t.metric.value)}</b> {metricUnit(t.metric.kind)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {t.metric && tt.kind === t.metric.kind && (
                                                            <VsBar value={t.metric.value} avg={tt.avg} max={Math.max(tt.max, t.metric.value) || 1} color={TYPE_COLORS[t.type]} kind={t.metric.kind} />
                                                        )}
                                                        {/* the classic per-employee statistic: per day · per hour · labor cost per unit */}
                                                        <div className="wps-type__stat">
                                                            {e.totalDays > 0 && <span><b>{fmtN(Math.round(t.units / e.totalDays))}</b> /day</span>}
                                                            {e.totalHours > 0 && <span><b>{r1(t.units / e.totalHours)}</b> /h</span>}
                                                            {e.pay > 0 && <span title="Labor cost per unit: pay ÷ units (all pay attributed to this type)"><b>${(e.pay / t.units).toFixed(e.pay / t.units < 0.1 ? 4 : 2)}</b> /unit</span>}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {showPay && (
                                        <div className="wps-card__foot">
                                            <span className="wps-muted">Earnings{e.totalDays > 0 ? ` · ${e.totalDays} day${e.totalDays === 1 ? '' : 's'}` : ''}</span>
                                            <span><b>{e.pay ? fmtMoney(e.pay) : '—'}</b>{e.pay > 0 && e.totalHours > 0 && <span className="wps-muted"> · {fmtMoney(e.pay / e.totalHours)}/h</span>}</span>
                                        </div>
                                    )}

                                    {open && (
                                        <div className="wps-weeks">
                                            <table className="wps-weeks__table">
                                                <thead><tr><th>Week of</th><th>Hours</th><th>Units</th><th className="wps-weeks__mixcol">Mix</th><th>Norm</th></tr></thead>
                                                <tbody>
                                                    {[...e.weeks].sort((a, b) => b.yearweek - a.yearweek).map(w => {
                                                        const total = TYPES.reduce((s, t) => s + (w.production[t] || 0), 0);
                                                        const shifts = Object.values(w.norm || {}).reduce((s, n) => s + (n.shifts || 0), 0);
                                                        const norm = w.hours > 0 && shifts > 0 ? Math.round((shifts / (w.hours / SHIFT_HOURS)) * 100) : null;
                                                        return (
                                                            <tr key={w.yearweek}>
                                                                <td>{fmtWeek(w.weekStart)}</td>
                                                                <td>{fmtH(w.hours)}</td>
                                                                <td>{total ? fmtN(total) : '—'}</td>
                                                                <td className="wps-weeks__mixcol"><MixBar production={w.production} total={total} height={6} /></td>
                                                                <td>{norm != null ? `${norm}%` : (w.hours > 0 && total ? `${r1(total / w.hours)} u/h` : '—')}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Productivity by type: fair comparison inside one product type */}
                    {usedTypes.length > 0 && (
                        <div className="wps-section">
                            <div className="wps-section__title">Productivity by product type <span className="wps-muted">% of norm (or units/h), hairline = team average</span></div>
                            <div className="wps-bytype">
                                {usedTypes.map(type => {
                                    const tt = team.types[type];
                                    const list = raw.map(e => {
                                        const s = sumType(e, type);
                                        const m = metricOf(type, s, e.totalHours);
                                        return m && m.kind === tt.kind ? { id: e.employeeID, name: e.employeeName, value: m.value, units: s.units } : null;
                                    }).filter(Boolean).sort((a, b) => b.value - a.value);
                                    const max = list[0] ? list[0].value : 1;
                                    return (
                                        <div key={type} className="wps-glass wps-bytype__panel">
                                            <div className="wps-bytype__head"><span className="wps-dot" style={{ background: TYPE_COLORS[type] }} />{TYPE_LABELS[type]}<span className="wps-muted"> · avg {fmtMetric(tt.kind, tt.avg)} {metricUnit(tt.kind)}</span></div>
                                            {list.slice(0, 8).map(x => (
                                                <div key={x.id} className={`wps-bytype__row${employeeId && String(x.id) !== String(employeeId) ? ' wps-bytype__row--dim' : ''}`} title={`${x.name}: ${fmtN(x.units)} units, ${fmtMetric(tt.kind, x.value)} ${metricUnit(tt.kind)}`}>
                                                    <span className="wps-bytype__name">{x.name}</span>
                                                    <VsBar value={x.value} avg={tt.avg} max={max} color={TYPE_COLORS[type]} kind={tt.kind} />
                                                    <span className="wps-bytype__val">{fmtMetric(tt.kind, x.value)}</span>
                                                </div>
                                            ))}
                                            {list.length > 8 && <div className="wps-muted wps-bytype__more">+{list.length - 8} more</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default WorkPerformanceStats;
